import React, { useContext } from 'react'
import { Plural, t, Trans } from '@lingui/macro'
import { FollowResourceType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { Box, Flex, FullBlockSpinner } from 'front-end/src/components'
import { CommentsFooter } from '../../components/common/comments/comments-footer/CommentsFooter'
import Rating from '../../components/common/rating/Rating'
import UpNext from '../topic-detail-page/components/UpNext'
import TopicVersionTitleAndSummary from '../topic-detail-page/TopicVersionTitleAndSummary'
import TextElement from '../topic-detail-page/topic-elements/TextElement'
import './ScormPage.less'
import { TopicDetailContext } from '../topic-detail-page/TopicDetailContext'
import { IconNewTab } from 'front-end/src/components/common/icon/IconNewTab'
import { brandColors } from 'shared/basicPalette'
import TriangleTop from 'front-end/src/pages/topic-detail-page/components/TriangleTop'
import QuizScore from 'front-end/src/pages/topic-detail-page/components/quizes/quizScore/QuizScore'
import {
  fontSizes,
  fontWeights
} from 'front-end/src/components/chakraThemeExtension'
import IconEye from 'front-end/src/components/common/icon/IconEye'
import IconReload from 'front-end/src/components/common/icon/IconReload'
import { useGetScormQuery } from 'front-end/src/pages/scorm-page/hooks/useGetScormQuery'
import { StartScormCourseButton } from 'front-end/src/pages/scorm-page/components/StartScormCourseButton'
import TopicCPDMarkToggle from 'front-end/src/pages/topic-detail-page/components/TopicCPDMarkToggle'

export type ScormPageProps = {
  shareId: number
  isPathway: boolean
}

declare global {
  interface Window {
    LOOOP: any
    ReactNativeWebView: any
  }
}

export const ScormPage = ({ shareId, isPathway }: ScormPageProps) => {
  const { topic, pathway, nextInPathway } = useContext(TopicDetailContext)
  const { data, loading, link, refetch } = useGetScormQuery(shareId, {
    notifyOnNetworkStatusChange: true
  })

  if (loading) {
    return <FullBlockSpinner />
  }

  const scormEnrolment =
    data.enrolment?.scormRegistration?.progressDetails?.topicEnrolment

  const isEnrolmentCompleted = !!scormEnrolment?.completion_date
  const isWithQuizzes =
    scormEnrolment?.auto_correct >= 0 && scormEnrolment?.available_points !== 0
  const maxAttempts = data.enrolment?.topicVersion?.max_attempts
  const currentAttempt = data?.enrolment?.attempt || 1
  const attemptsLeft = maxAttempts
    ? maxAttempts - (isEnrolmentCompleted ? currentAttempt : currentAttempt - 1)
    : null
  const passed =
    scormEnrolment?.pass_mark &&
    scormEnrolment?.auto_correct >= scormEnrolment?.pass_mark
  const allowRetry = data?.enrolment?.topicVersion.allow_retry

  const Progress = () => {
    return (
      <>
        {scormEnrolment && isEnrolmentCompleted && (
          <>
            <Box mb="40px">
              <Flex align="center" justify="center" py="20px" mb="10px">
                {isWithQuizzes && (
                  <QuizScore
                    percentages={Number(
                      (
                        (scormEnrolment.auto_correct /
                          scormEnrolment.available_points) *
                        100
                      ).toFixed(2)
                    )}
                    tagText={passed ? t`Passed` : t`Failed`}
                    tagColor={passed ? 'green' : 'red'}
                  />
                )}

                <Box
                  ml="24px"
                  size="lg"
                  fontSize={fontSizes['2xl']}
                  fontWeight={fontWeights.medium}
                >
                  {isWithQuizzes ? (
                    <>
                      {passed ? (
                        <Trans>You have passed the course</Trans>
                      ) : (
                        <Trans>You have not passed the course</Trans>
                      )}
                    </>
                  ) : (
                    <Trans>You have completed this course</Trans>
                  )}
                </Box>
              </Flex>

              {(passed || !isWithQuizzes) && (
                <StartScormCourseButton topicId={topic.id} link={link}>
                  <IconEye mr="8px" />{' '}
                  {/* don't delete this span, it's essential (if page is translated, and button is clicked it throws error otherwise)*/}
                  <span>
                    <Trans>Review this course</Trans>
                  </span>
                </StartScormCourseButton>
              )}
              {isWithQuizzes && !passed && allowRetry && (
                <>
                  {attemptsLeft !== 0 && (
                    <StartScormCourseButton
                      topicId={topic.id}
                      link={link}
                      consumeAttempt
                      onConsumeAttempt={async (newEnrolment) => {
                        const { data } = await refetch({
                          id: newEnrolment.topic.reEnrol.id
                        })
                        window.open(
                          `${data?.enrolment?.getOrCreateScormRegistration?.launchLink}` ||
                            '',
                          '_self'
                        )
                      }}
                    >
                      <IconReload />{' '}
                      {/* don't delete this span, it's essential (if page is translated, and button is clicked it throws error otherwise)*/}
                      <span>
                        <Trans>Retry this course</Trans>
                      </span>
                    </StartScormCourseButton>
                  )}
                  {!!attemptsLeft && attemptsLeft > 0 && (
                    <Box mt="8px" color={brandColors.grey.mid}>
                      <Plural
                        value={attemptsLeft}
                        one="# attempt left"
                        other="# attempts left"
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
            {!scormEnrolment.rating && <Rating enrolmentId={shareId} />}
            {typeof topic.enrolmentOrSelfEnrol?.isCpdEntry === 'boolean' && (
              <TopicCPDMarkToggle
                initialCpdStatus={topic.enrolmentOrSelfEnrol.isCpdEntry}
                topicEnrolmentId={topic.enrolmentOrSelfEnrol.id}
                key={topic.enrolmentOrSelfEnrol.id}
              />
            )}
          </>
        )}
        {!isEnrolmentCompleted && (
          <h3 className="Footer__completed">
            <Trans>You have not yet completed the resource</Trans>
          </h3>
        )}
      </>
    )
  }

  return (
    <div className="ScormPage">
      <div className="ScormPage__inner">
        <div className="page page-system-generated-title">
          {
            <TopicVersionTitleAndSummary
              title={data.enrolment.topicVersion?.title}
              summary={data.enrolment.topicVersion?.summary}
            />
          }
        </div>
        {data.enrolment?.topicVersion?.publishedElements?.length === 1 && (
          <div
            className={`ql-editor page-content page-content-text element element-text element-mode-view`}
          >
            <TextElement
              content={
                data.enrolment.topicVersion?.publishedElements[0].data
                  .content as string
              }
            />
          </div>
        )}
        <div className="ScormPage__course">
          {!isEnrolmentCompleted && (
            <StartScormCourseButton topicId={topic.id} link={link}>
              <IconNewTab mr="8px" />{' '}
              {/* don't delete this span, it's essential (if page is translated, and button is clicked it throws error otherwise)*/}
              <span>
                <Trans>Launch the course</Trans>
              </span>
            </StartScormCourseButton>
          )}

          {!isEnrolmentCompleted && !!attemptsLeft && attemptsLeft > 0 && (
            <Box mt="8px" color={brandColors.grey.mid}>
              <Plural
                value={attemptsLeft}
                one="# attempt left"
                other="# attempts left"
              />
            </Box>
          )}
        </div>
        <div className="Footer">
          <Box className="topic-completion-page pageElement learn" pb={0}>
            {!isEnrolmentCompleted && (
              <Box className="topic-completion-alert" pb="20px">
                <TriangleTop backgroundColor="#e3e8ea" />
                <h2>
                  <Trans>Please launch the course</Trans>
                </h2>
                <p>
                  <Trans>
                    You will not have completed this resource until you complete
                    this course
                  </Trans>
                </p>
              </Box>
            )}
            {data?.enrolment?.scormRegistration && <Progress {...data} />}
          </Box>

          {isPathway &&
            pathway &&
            nextInPathway &&
            topic &&
            nextInPathway.id !== topic.id &&
            isEnrolmentCompleted && (
              <div className="upnext-page-wrapper">
                <UpNext pathway={pathway} topic={nextInPathway} />
              </div>
            )}
          <CommentsFooter
            itemType={FollowResourceType.topic}
            itemId={data.enrolment.topic.id}
            allowComments={
              data.enrolment.topicVersion?.topic.allow_comments || false
            }
          />
        </div>
      </div>
    </div>
  )
}
